<template>
  <div v-if="isLoaded" :class="$style.sign_in">
    <MoleculesConsumerHeading title="Login" sub-title="ログイン" />
    <p v-if="isLocked" :class="$style.error_message">
      複数回のログイン失敗が確認されたため、ご利用のアカウントはロックされています。<br />
      しばらく待ってから再度お試しいただくか、
      <AtomsConsumerLink url="/password_forget">パスワード変更ページ</AtomsConsumerLink>
      よりパスワードの変更を行ってください。
    </p>

    <div v-else>
      <p v-if="isLineSignIn">既に会員の方はログイン後、会員情報からLINE連携を行なって下さい</p>

      <form :class="$style.sign_in_form" @submit.prevent="signIn">
        <div v-if="isReturnExchange">
          <template v-if="isGiftReceipt">
            <div :class="$style.message">
              <h3>ギフトレシートからの商品交換について</h3>
              <p>
                商品の交換をご希望の方は、以下の手順でお手続きをお願いいたします。<br />
                ・会員の方はログイン後、交換手続きを進めてください<br />
                ・会員でない方は、<AtomsConsumerLink :class="$style.link" :url="`/sign_up`"
                  >新規会員登録</AtomsConsumerLink
                >を行ってから交換手続きを進めてください
              </p>
            </div>
          </template>
          <template v-else>
            <div :class="$style.message">
              <h3>返品交換をご希望の方</h3>
              <p>購入時に設定したメールアドレスとパスワードを入力ください</p>
            </div>
            <div :class="$style.message">
              <h3>eギフト商品の返品交換をご希望の方</h3>
              <p>
                <AtomsConsumerLink :url="`/contact`">お問い合わせ窓口</AtomsConsumerLink>
                よりご連絡ください
              </p>
            </div>
          </template>
        </div>

        <MoleculesConsumerFormItem :error="!!errorMessage.email" :error-message="errorMessage.email">
          <template #title>メールアドレス</template>
          <AtomsConsumerInput id="email" v-model="form.email" type="email" placeholder="例）tential@e-mail.com" />
        </MoleculesConsumerFormItem>

        <MoleculesConsumerFormItem :error="!!errorMessage.password" :error-message="errorMessage.password">
          <template #title>パスワード</template>
          <AtomsConsumerInput id="password" v-model="form.password" type="password" />
          <template #footer-description>
            <AtomsConsumerLink url="/password_forget">パスワードを忘れた方はこちら</AtomsConsumerLink>
          </template>
        </MoleculesConsumerFormItem>

        <div :class="$style.buttons">
          <AtomsConsumerButton type="submit" variant="primary" block>ログイン</AtomsConsumerButton>
        </div>
        <div :class="$style.other_sign_in_buttons">
          <CommonLineLoginButton v-if="!isLineSignIn" :redirect-url="`/sign_in${redirectQuery}`" />
          <div :id="amazonPayButtonId" style="height: 45px"></div>
        </div>
      </form>
    </div>

    <div :class="$style.sign_up">
      <h2>新規アカウント作成</h2>
      <p v-if="isLineSignIn">
        会員でない方は新規会員登録後、会員情報からLINE連携を行なうか、LINEで会員登録を行なって下さい
      </p>
      <p v-else>無料会員登録で今すぐお買い物をお楽しみいただけます</p>
      <AtomsConsumerLink :url="signUpPath">
        <AtomsConsumerButton variant="primary" block>新規会員登録</AtomsConsumerButton>
      </AtomsConsumerLink>
    </div>
  </div>
  <OrganismsLoading v-else />
</template>

<script setup lang="ts">
import AmazonPayClient from "~/pageScripts/amazonpay"
import useAuth from "~/composables/useAuth"
import { commonErrorHandleForTryCatch } from "~/utils/functions/error"
import { useToast } from "~/state"
import { useCustomMeta } from "~/composables/meta"
import { useStore } from "~/stores"
import { getFullRedirectPath } from "~/utils/functions/url"

const customMeta = useCustomMeta()
const route = useRoute()
const form = reactive({ email: "", password: "" })

const errorMessage = ref({
  email: "",
  password: "",
})
const router = useRouter()
const toast = useToast()
const store = useStore()
const config = useRuntimeConfig()
const isLocked = ref(false)
const isLoaded = ref<boolean>(false)

const { signIn: innerSignIn, socialSign, amazonPaySignIn } = useAuth()

const redirectPath = getFullRedirectPath(route.query.next as string, route)

const offlinePath = route.query.redirect_path ? decodeURI(route.query.redirect_path.toString()) : ""
const offlinePathForLine = "/mypage?offline_store=true"

const signUpPath = route.query.next ? `/sign_up?next=${redirectPath}` : "/sign_up"

const amazonPayButtonId = "AmazonPayLoginButton"

const { SITE_SLUG } = useSite()

const isLineSignIn = computed(() => {
  return redirectPath.includes("line=true")
})

const redirectQuery = computed(() => {
  return redirectPath ? `?next=${redirectPath}` : ""
})

/**
 * 返品交換ページへリダイレクトされているか
 */
const isReturnExchange = computed(() => {
  return redirectPath.includes("return_or_exchange")
})

/**
 * ギフトレシートからリダイレクトされているか
 */
const isGiftReceipt = computed(() => {
  return redirectPath.includes("grhId")
})

const validate = (): boolean => {
  if (!form.email) {
    errorMessage.value.email = "メールアドレスを入力してください"
  }
  if (!form.password) {
    errorMessage.value.password = "パスワードを入力してください"
  }

  return !!form.email && !!form.password
}

const signIn = async () => {
  const isValidate = validate()
  if (!isValidate) return
  const { error, errMsg } = await innerSignIn(form.email, form.password)
  if (error || errMsg) {
    if (errMsg?.includes("email or password is invalid"))
      return toast?.showErrorToasted("パスワードかメールアドレスが間違っています")
    if (errMsg?.includes("try another login method"))
      return toast?.showErrorToasted("他のSNSで会員登録されている可能性があります。別のログイン方法をお試しください")
    if (errMsg?.includes("account is locked")) {
      isLocked.value = true
      return toast?.showErrorToasted("アカウントがロックされました")
    }
    return toast?.showErrorToasted("エラーが発生しました")
  }
  await navigateTo(offlinePath === "" ? redirectPath : offlinePath)

  toast?.showSuccessToasted("ログインに成功しました")
}

const checkSocialSign = async () => {
  try {
    const token = String(route.query.token)
    const isOffline = store.routeLogs.some((route) => route.url.includes("offline_store"))
    const data = await socialSign(SITE_SLUG, token)
    if (data.error || !data.body) {
      if (data.error?.toString().includes("トークンが有効ではありません。時間をおいて再度お試しください"))
        toast?.showErrorToasted("トークンが有効ではありません。時間をおいて再度お試しください")
      else toast?.showErrorToasted("ログインに失敗しました.再度ログインを行なってください")
      return
    }
    if (data.body && !data.body.is_login) {
      toast?.showErrorToasted("LINE連携されているユーザーが見つかりません。新規登録を行なってください")
      return
    }
    router.push(isOffline ? offlinePathForLine : redirectPath)
    toast?.showSuccessToasted("ログインに成功しました")
  } catch (error) {
    commonErrorHandleForTryCatch(error, toast, "socialSign", "ログインに失敗しました.再度ログインを行なってください")
  }
}

// クエリにbuyerTokenがある場合はAmazonPayログイン処理
const checkAmazonPaySignIn = async () => {
  try {
    const buyerToken = String(route.query.buyerToken)
    const data = await amazonPaySignIn(buyerToken)
    if (!data.body || data.status !== 200) {
      if (data.error === "already registered email") {
        toast?.showErrorToasted("このメールアドレスは登録済みです。メールアドレスとパスワードでログインしてください")
      } else {
        toast?.showErrorToasted("ログインに失敗しました")
      }
    } else {
      if (data.body.isLogin) {
        toast?.showSuccessToasted("Amazon Payでログインに成功しました")
      } else {
        toast?.showSuccessToasted("Amazon Payで新規登録に成功しました")
      }
      router.push(redirectPath)
    }
  } catch (error) {
    commonErrorHandleForTryCatch(error, toast, "checkAmazonPaySignIn", "ログインに失敗しました")
  }
}

// サインインボタン用の署名を取得
const { amazonPaySignature } = await $fetch<{ amazonPaySignature: string }>(
  "/api/services/amazon_pay/sign_in/signature",
  {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  },
)

const mountAmazonPayButton = () => {
  const amazonPayClient = new AmazonPayClient({ id: amazonPayButtonId })
  amazonPayClient.renderButton({
    placement: "Other",
    ledgerCurrency: "JPY",
    checkoutLanguage: "ja_JP",
    productType: "SignIn",
    // process.env.NODE_ENV !== "production" だとNODE_ENV指定されてない場合にSANDBOX環境にならないため
    sandbox: !(config.public.APP_ENV === "production"),
    signature: amazonPaySignature,
  })
}

onMounted(async () => {
  if (route.query.token) await checkSocialSign()
  if (route.query.buyerToken) await checkAmazonPaySignIn()
  await mountAmazonPayButton()
  isLoaded.value = true
})

useHead(
  customMeta.fetch({
    title: `ログイン`,
  }),
)
</script>

<style lang="scss" scoped module>
.sign_in {
  .message {
    margin: 1rem 0;
    h3 {
      margin-bottom: 0.5rem;
      font-size: $font-size-16;
      font-weight: 600;
      color: $primary;
    }
    p {
      font-size: $font-size-14;
      .link {
        color: $primary;
        text-decoration: underline;
      }
    }
  }
  .sign_in_form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 1rem;
    @include md {
      max-width: $max-width-sm;
      margin: 1rem auto;
    }
    .buttons {
      margin-top: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      @include md {
        flex-direction: row;
        gap: 1rem;
      }
    }
    .other_sign_in_buttons {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      @include md {
        flex-direction: row;
        gap: 1rem;
        & > * {
          width: calc(50% - 0.25rem);
        }
      }
    }
  }
  .sign_up {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 2rem;
    margin: 3rem 1rem 0;
    border-radius: 0.25rem;
    background-color: $primary-10;
    @include md {
      padding: 4rem;
      max-width: $max-width-sm;
      margin: 4rem auto;
    }
    h2 {
      font-size: $font-size-18;
      font-weight: 600;
      color: $primary;
    }
  }
  .error_message {
    font-weight: bold;
    color: $red;
    margin: 1rem;
    @include md {
      max-width: $max-width-sm;
      margin: 1rem auto;
    }
  }
  a {
    color: $primary;
    text-decoration: underline;
  }
}
</style>
